import styles from './KTMDeployer.module.scss'
import useTranslation from '../Hooks/useTranslation'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark, faCheck } from '@fortawesome/free-solid-svg-icons'
import { faObjectGroup, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons'
import Button from './Button'
import { Checkbox, States as CheckboxStates } from './Checkbox'
import usePrefStore from '../stores/prefs'
import { useEffect, useMemo, useState } from 'react'
import useHelpers from '../Hooks/useHelpers'
import useSales from '../Hooks/useSales'
import useRetailQuota from '../Hooks/useRetailQuota'
import glob_const from '../libs/glob_const'
import type { Order, Customer, KTMClientType } from '../types'

export type KTMDeployOptions = {
   /** Pour les SKUs présents dans le KTM ET dans le KOT existant : remplacer la qté du KOT par celle du KTM. */
   replaceExistingQty: boolean
   /** Pour les SKUs présents dans le KTM mais absents du KOT existant : les ajouter au KOT. */
   addNewLines: boolean
   /** Pour les SKUs présents dans le KOT mais absents du KTM : les retirer du KOT. */
   removeAbsentLines: boolean
}

interface KTMDeployerProps {
   ktm: Order
   onCancel: () => void
   onValidate: (selectedCustomerIds: string[], opts: KTMDeployOptions) => Promise<void> | void
   top?: string
   minWidth?: string
   maxWidth?: string
}

type RowInfo = {
   customer: Customer
   hasKOT: boolean   // KOT existe déjà pour cette période
   selected: boolean
}

function KTMDeployer(props: KTMDeployerProps) {
   const { trans } = useTranslation()
   const { inlineStyleFromProps } = useHelpers()
   const { globalClosePopups } = usePrefStore()
   const { getCustomersAsSortedArray } = useSales()
   const { extractClientTypeFromKTMMark, extractPeriodFromKTMMark, findKOTByPeriod, KTM_CLIENT_TYPES } = useRetailQuota()

   const [addStyle, setAddStyle] = useState<any>({})
   const [rows, setRows] = useState<RowInfo[]>([])
   const [isLoading, setIsLoading] = useState(true)
   const [isDeploying, setIsDeploying] = useState(false)
   // Options de fusion côté KOTs existants (par défaut : remplacement total)
   const [optReplaceQty, setOptReplaceQty] = useState(true)
   const [optAddNew, setOptAddNew] = useState(true)
   const [optRemoveAbsent, setOptRemoveAbsent] = useState(true)

   const clientType = useMemo<KTMClientType | null>(() => extractClientTypeFromKTMMark(props.ktm.mark ?? null), [extractClientTypeFromKTMMark, props.ktm.mark])
   const period = useMemo(() => extractPeriodFromKTMMark(props.ktm.mark ?? null), [extractPeriodFromKTMMark, props.ktm.mark])
   const isEmpty = (props.ktm.lines?.length ?? 0) === 0

   useEffect(() => {
      const style = inlineStyleFromProps({ top: props.top, minWidth: props.minWidth, maxWidth: props.maxWidth })
      setAddStyle(style)
   }, [props.top, props.minWidth, props.maxWidth, inlineStyleFromProps])

   useEffect(() => {
      let cancelled = false
      const load = async () => {
         setIsLoading(true)
         if (!clientType) {
            setRows([])
            setIsLoading(false)
            return
         }
         // Filtrer les clients dont le groupe_client correspond au type du KTM
         const all = getCustomersAsSortedArray()
         const eligible = all.filter(c => {
            if (!c.groupe_client) return false
            if (c.id?.startsWith(glob_const.prefix_ktm) || c.id?.startsWith(glob_const.prefix_prospect)) return false
            const grp = (glob_const.customer_groups as Record<string, { type: KTMClientType }>)[c.groupe_client]
            return grp?.type === clientType
         })
         // Pour chaque customer, check si KOT existe déjà pour la période
         const results = await Promise.all(eligible.map(async (c) => {
            const kot = await findKOTByPeriod(c.id!, period)
            return { customer: c, hasKOT: !!kot, selected: true } as RowInfo
         }))
         if (!cancelled) {
            setRows(results)
            setIsLoading(false)
         }
      }
      load()
      return () => { cancelled = true }
   }, [clientType, period, getCustomersAsSortedArray, findKOTByPeriod])

   const selectedCount = rows.filter(r => r.selected).length
   const withKotCount = rows.filter(r => r.hasKOT).length
   const selectedConflictCount = rows.filter(r => r.selected && r.hasKOT).length

   function doNothing(evt) {
      evt.stopPropagation()
   }

   function onClickBackground() {
      if (props.onCancel && globalClosePopups !== 'button' && !isDeploying) {
         props.onCancel()
      }
   }

   function toggleRow(id: string) {
      setRows(rows => rows.map(r => r.customer.id === id ? { ...r, selected: !r.selected } : r))
   }

   function selectOnlyMissing() {
      setRows(rows => rows.map(r => ({ ...r, selected: !r.hasKOT })))
   }

   function selectOnlyExisting() {
      setRows(rows => rows.map(r => ({ ...r, selected: r.hasKOT })))
   }

   // Case "maître" : reflète tout / rien / certains et bascule entre Tout et Rien
   const masterState = useMemo(() => {
      if (rows.length === 0) return CheckboxStates.Unchecked
      const nbSel = rows.filter(r => r.selected).length
      if (nbSel === 0) return CheckboxStates.Unchecked
      if (nbSel === rows.length) return CheckboxStates.Checked
      return CheckboxStates.Undefined
   }, [rows])

   function toggleMaster() {
      // Si tout ou certains sont cochés → on décoche tout. Sinon on coche tout.
      const shouldCheckAll = masterState !== CheckboxStates.Checked
      setRows(rows => rows.map(r => ({ ...r, selected: shouldCheckAll })))
   }

   async function doValidate() {
      if (isDeploying) return
      setIsDeploying(true)
      try {
         const ids = rows.filter(r => r.selected).map(r => r.customer.id!).filter(Boolean)
         await props.onValidate(ids, {
            replaceExistingQty: optReplaceQty,
            addNewLines: optAddNew,
            removeAbsentLines: optRemoveAbsent,
         })
      } finally {
         setIsDeploying(false)
      }
   }

   const anyRowHasKOT = withKotCount > 0
   const selectedHasKOT = rows.some(r => r.selected && r.hasKOT)

   const nbLines = props.ktm.lines?.length ?? 0
   const title = clientType
      ? `${trans('?Déployer')} ${KTM_CLIENT_TYPES[clientType]}`
      : trans('?Déployer')
   const subtitle = `${props.ktm.mark ?? ''}  —  ${nbLines} ${trans('?ligne', nbLines)}`

   return (
      <div className={styles.background} onClick={onClickBackground}>
         <div className={styles.ktm_deployer} onClick={doNothing} style={addStyle}>
            <div className={styles.top}>
               <FontAwesomeIcon icon={faObjectGroup} />
               <span>{title}</span>
               <span className={styles.subtitle}>{subtitle}</span>
               <button className={styles.close} onClick={props.onCancel}>
                  <FontAwesomeIcon icon={faXmark} />
               </button>
            </div>
            <div className={styles.toolbar}>
               <Button small tertiary onClick={selectOnlyMissing}>{trans('?Sans quota')}</Button>
               <Button small tertiary onClick={selectOnlyExisting} disabled={withKotCount === 0}>{trans('?Avec quotas')}</Button>
               <span className={styles.count}>
                  {selectedCount} / {rows.length} {trans('?sélectionné', selectedCount)}
                  {withKotCount > 0 && ` — ${withKotCount} ${trans('?déjà avec quotas', withKotCount)}`}
               </span>
               {selectedConflictCount > 0 && (
                  <span className={styles.conflict_summary}>
                     <FontAwesomeIcon icon={faTriangleExclamation} />
                     {selectedConflictCount} {trans('?collision', selectedConflictCount)}
                  </span>
               )}
            </div>
            <div className={styles.list}>
               {isLoading ? (
                  <div className={styles.empty}>{trans('?Chargement...')}</div>
               ) : rows.length === 0 ? (
                  <div className={styles.empty}>{trans('?Aucun client éligible')}</div>
               ) : (
                  <table>
                     <thead>
                        <tr>
                           <th className={styles.col_checkbox} onClick={e => e.stopPropagation()}>
                              <Checkbox
                                 small
                                 three_states
                                 checked={masterState}
                                 onChange={toggleMaster}
                              />
                           </th>
                           <th className={styles.col_id}>N°</th>
                           <th>{trans('?Client')}</th>
                           <th className={styles.col_status}>{trans('?Quotas existants')}</th>
                        </tr>
                     </thead>
                     <tbody>
                        {rows.map(r => {
                           // Une "collision" = il existe déjà un KOT pour ce client à cette même période (mark/date début).
                           // Les options en bas du popup déterminent comment fusionner ; on alerte avant validation.
                           const isConflict = r.hasKOT && r.selected
                           return (
                              <tr
                                 key={r.customer.id}
                                 onClick={() => toggleRow(r.customer.id!)}
                                 className={isConflict ? styles.row_conflict : ''}
                              >
                                 <td className={styles.col_checkbox}>
                                    <Checkbox
                                       small
                                       checked={r.selected ? CheckboxStates.Checked : CheckboxStates.Unchecked}
                                       onChange={() => toggleRow(r.customer.id!)}
                                    />
                                 </td>
                                 <td className={styles.col_id}>{r.customer.id}</td>
                                 <td>{r.customer.name}</td>
                                 <td className={styles.col_status}>
                                    {r.hasKOT && (
                                       <span className={styles.conflict_badge} title={trans('?Quota existant pour cette période — sera modifié selon les options ci-dessous')}>
                                          <FontAwesomeIcon icon={faTriangleExclamation} />
                                          {trans('?Collision')}
                                       </span>
                                    )}
                                 </td>
                              </tr>
                           )
                        })}
                     </tbody>
                  </table>
               )}
            </div>
            {!isEmpty && anyRowHasKOT && (
               <div className={styles.options}>
                  <div className={styles.options_title}>{trans('?Pour les clients ayant déjà des quotas :')}</div>
                  <Checkbox
                     small
                     checked={optReplaceQty ? CheckboxStates.Checked : CheckboxStates.Unchecked}
                     caption={trans('?Remplacer les quantités existantes')}
                     onChange={(s) => setOptReplaceQty(s === CheckboxStates.Checked)}
                  />
                  <Checkbox
                     small
                     checked={optAddNew ? CheckboxStates.Checked : CheckboxStates.Unchecked}
                     caption={trans('?Ajouter les nouvelles lignes')}
                     onChange={(s) => setOptAddNew(s === CheckboxStates.Checked)}
                  />
                  <Checkbox
                     small
                     checked={optRemoveAbsent ? CheckboxStates.Checked : CheckboxStates.Unchecked}
                     caption={trans('?Supprimer les lignes absentes')}
                     onChange={(s) => setOptRemoveAbsent(s === CheckboxStates.Checked)}
                  />
                  {selectedHasKOT && !optReplaceQty && !optAddNew && !optRemoveAbsent && (
                     <div className={styles.options_hint}>
                        <FontAwesomeIcon icon={faTriangleExclamation} />
                        <span>{trans('?Aucune option : les quotas existants ne seront pas modifiés.')}</span>
                     </div>
                  )}
               </div>
            )}
            {isEmpty && selectedCount > 0 && (
               <div className={styles.warning}>
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  <span>{trans('?KTM vide : les quotas existants des clients sélectionnés pour cette période seront supprimés.')}</span>
               </div>
            )}
            <div className={styles.footer}>
               <Button tertiary lefticon={<FontAwesomeIcon icon={faXmark} />} onClick={props.onCancel} disabled={isDeploying}>
                  {trans('?Annuler')}
               </Button>
               <Button disabled={selectedCount === 0 || isDeploying} lefticon={<FontAwesomeIcon icon={faCheck} />} onClick={doValidate}>
                  {isEmpty ? trans('?Supprimer') : trans('?Déployer')}
               </Button>
            </div>
         </div>
      </div>
   )
}

export default KTMDeployer
